import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

import { useApiClient } from '../../hooks/useApiClient';
import type { DeploymentLocationSummary } from '../../types';

interface CreateLocationForm {
  name: string;
  label: string;
  description: string;
}

const INITIAL_FORM: CreateLocationForm = {
  name: '',
  label: '',
  description: '',
};

const filenameSafe = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'location';

export default function DeploymentLocationsPage() {
  const apiClient = useApiClient();
  const [locations, setLocations] = useState<DeploymentLocationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateLocationForm>(INITIAL_FORM);
  const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadLocations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient('/api/admin/locations');
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const payload = (await response.json()) as DeploymentLocationSummary[];
      setLocations(payload);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Unable to load deployment locations from the backend.');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [locations],
  );

  const handleCreateLocation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.label.trim()) {
      toast.error('Name and label are required');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient('/api/admin/locations', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          label: form.label.trim(),
          description: form.description.trim() || undefined,
          isActive: true,
        }),
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || 'Unable to create deployment location');
      }
      const payload = (await response.json()) as DeploymentLocationSummary;
      setLocations((prev) => [...prev, payload]);
      setForm(INITIAL_FORM);
      setExpandedLocation(payload.id);
      toast.success(`Added deployment location ${payload.label}`);
    } catch (submitError) {
      console.error(submitError);
      toast.error(submitError instanceof Error ? submitError.message : 'Failed to create deployment location');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleLocation = async (location: DeploymentLocationSummary) => {
    try {
      const response = await apiClient(`/api/admin/locations/${location.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !location.isActive }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const updated = (await response.json()) as DeploymentLocationSummary;
      setLocations((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(`${updated.label} ${updated.isActive ? 'enabled' : 'disabled'}`);
    } catch (toggleError) {
      console.error(toggleError);
      toast.error('Unable to update deployment location');
    }
  };

  const handleDeleteLocation = async (location: DeploymentLocationSummary) => {
    const confirmed = window.confirm(`Remove "${location.label}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }
    try {
      const response = await apiClient(`/api/admin/locations/${location.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      setLocations((prev) => prev.filter((item) => item.id !== location.id));
      toast.success(`Removed deployment location ${location.label}`);
    } catch (deleteError) {
      console.error(deleteError);
      toast.error('Unable to delete deployment location');
    }
  };

  const handleDownloadQr = (location: DeploymentLocationSummary) => {
    const canvas = document.getElementById(`location-qr-${location.id}`) as HTMLCanvasElement | null;
    if (!canvas) {
      toast.error('QR code not ready');
      return;
    }
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `location-${filenameSafe(location.label)}.png`;
      link.click();
    } catch (error) {
      console.error(error);
      toast.error('Unable to download QR code');
    }
  };

  return (
    <div className="admin-section">
      <header className="admin-section-header">
        <div>
          <h1>Deployment Locations</h1>
          <p>Manage QR codes for scanning where calls originate.</p>
        </div>
      </header>

      <form className="admin-form" onSubmit={handleCreateLocation}>
        <h2>Add Deployment Location</h2>
        <div className="admin-form-grid">
          <label>
            <span>Unique Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="or-main"
              required
              disabled={isSubmitting}
            />
          </label>
          <label>
            <span>Label</span>
            <input
              type="text"
              value={form.label}
              onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
              placeholder="Operating Room - Main"
              required
              disabled={isSubmitting}
            />
          </label>
        </div>
        <label>
          <span>Description</span>
          <textarea
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            placeholder="Optional notes about hospital wing, floor, or restrictions"
            disabled={isSubmitting}
          />
        </label>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Add Location'}
        </button>
      </form>

      <section className="admin-list">
        <h2>Registered Locations</h2>
        {isLoading ? <p>Loading locations…</p> : null}
        {error ? <p className="admin-error">{error}</p> : null}
        {!isLoading && !error ? (
          sortedLocations.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '720px',
                }}
              >
                <thead>
                  <tr style={{ textAlign: 'left', color: '#cbd5f5' }}>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Label</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Name</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Status</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Last Used</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLocations.map((location) => {
                    const isExpanded = expandedLocation === location.id;
                    return (
                      <Fragment key={location.id}>
                        <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                          <td style={{ padding: '0.85rem 1rem', color: '#e2e8f0', fontWeight: 600 }}>{location.label}</td>
                          <td style={{ padding: '0.85rem 1rem', color: '#94a3b8' }}>{location.name}</td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <span className={location.isActive ? 'badge badge-success' : 'badge badge-neutral'}>
                              {location.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '0.85rem 1rem', color: '#94a3b8' }}>
                            {location.lastUsedAt ? new Date(location.lastUsedAt).toLocaleString() : '—'}
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => handleToggleLocation(location)}>
                                {location.isActive ? 'Disable' : 'Enable'}
                              </button>
                              <button type="button" onClick={() => setExpandedLocation(isExpanded ? null : location.id)}>
                                {isExpanded ? 'Hide Details' : 'Show Details'}
                              </button>
                              <button type="button" onClick={() => handleDownloadQr(location)}>
                                Download PNG
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteLocation(location)}
                                style={{
                                  border: '1px solid rgba(248, 113, 113, 0.4)',
                                  background: 'rgba(248, 113, 113, 0.15)',
                                  color: '#fecaca',
                                }}
                              >
                                Delete
                              </button>
                            </div>
                            <span style={{ display: 'none' }}>
                              <QRCodeCanvas
                                value={location.qrPayload}
                                size={132}
                                includeMargin
                                id={`location-qr-${location.id}`}
                              />
                            </span>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={5} style={{ padding: '1rem 1.25rem', background: 'rgba(15,23,42,0.65)' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', color: '#cbd5f5' }}>
                                {location.description ? (
                                  <p style={{ margin: 0, color: '#94a3b8' }}>{location.description}</p>
                                ) : null}
                                <div className="admin-qr-wrapper">
                                  <QRCodeSVG value={location.qrPayload} size={132} />
                                  <div>
                                    <p>Location</p>
                                    <code>{location.label}</code>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No deployment locations yet.</p>
          )
        ) : null}
      </section>
    </div>
  );
}
